import { useState, useEffect, useRef } from "react";
import { productApi, productCategoryApi, vendorApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";

/* ────────────────────────────────────────
   CONSTANTS
──────────────────────────────────────── */
const PRICING_TYPES = ["Fixed", "Per Gram", "Per Carat", "Per Piece"];
const PURITY_STANDARDS = ["24K", "22K", "18K", "14K", "925 Silver", "950 Platinum", "Other"];
const BARCODE_TYPES = ["QR Code", "Code128", "EAN-13", "Code39"];

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Product?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Product Detail Drawer ── */
function ProductDrawer({ product, categories, vendors, onClose, onEdit }) {
  const categoryName = categories.find(c => c.categoryID === product.categoryID)?.categoryName ?? "—";
  const vendorName = vendors.find(v => v.vendorID === product.vendorID)?.vendorName ?? "—";

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-product-drawer" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="rv-product-drawer-header">
          <div className="rv-product-drawer-avatar">
            {product.productName?.charAt(0).toUpperCase()}
          </div>
          <div className="rv-product-drawer-title-wrap">
            <h3 className="rv-product-drawer-name">{product.productName}</h3>
            <div className="rv-product-drawer-meta">
              <span className="rv-product-code-chip"><TagIcon /> {product.productCode || "—"}</span>
              <span className={`rv-status-pill ${product.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                <span className="rv-status-dot" />
                {product.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="rv-product-drawer-body">
          <div className="rv-product-detail-grid">
            <div className="rv-product-detail-item">
              <span className="rv-vendor-detail-label"><LayersIcon /> Category</span>
              <span className="rv-vendor-detail-val">{categoryName}</span>
            </div>
            <div className="rv-product-detail-item">
              <span className="rv-vendor-detail-label"><VendorIcon /> Vendor</span>
              <span className="rv-vendor-detail-val">{vendorName}</span>
            </div>
            <div className="rv-product-detail-item">
              <span className="rv-vendor-detail-label"><CurrencyIcon /> Pricing Type</span>
              <span className="rv-vendor-detail-val">{product.pricingType || "—"}</span>
            </div>
            <div className="rv-product-detail-item">
              <span className="rv-vendor-detail-label"><StarIcon /> Purity Standard</span>
              <span className="rv-vendor-detail-val">{product.purityStandard || "—"}</span>
            </div>
            <div className="rv-product-detail-item">
              <span className="rv-vendor-detail-label"><BarcodeIcon /> Barcode Type</span>
              <span className="rv-vendor-detail-val">{product.barcodeType || "—"}</span>
            </div>
            <div className="rv-product-detail-item">
              <span className="rv-vendor-detail-label"><HashIcon /> HSN Override</span>
              <span className="rv-vendor-detail-val">
                {product.codeHSNOverride
                  ? <span className="rv-product-hsn-yes">Yes — {product.codeHSN || "—"}</span>
                  : "No"}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="rv-product-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button className="rv-btn rv-btn--primary" onClick={() => { onEdit(product); onClose(); }}>
            <EditIcon /> Edit Product
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Product Form Modal ── */
function ProductModal({ initial, onSave, onClose, saving, categories, vendors }) {
  const isEdit = !!initial?.productID;

  const [form, setForm] = useState({
    productCode: initial?.productCode ?? "",
    productName: initial?.productName ?? "",
    categoryID: initial?.categoryID ?? "",
    vendorID: initial?.vendorID ?? "",
    pricingType: initial?.pricingType ?? "",
    purityStandard: initial?.purityStandard ?? "",
    barcodeType: initial?.barcodeType ?? "",
    isActive: initial?.isActive ?? true,
    codeHSNOverride: initial?.codeHSNOverride ?? false,
    codeHSN: initial?.codeHSN ?? "",
  });
  const [errors, setErrors] = useState({});

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.productName.trim()) e.productName = "Product name is required.";
    if (!form.productCode.trim()) e.productCode = "Product code is required.";
    if (!form.categoryID) e.categoryID = "Please select a category.";
    if (!form.vendorID) e.vendorID = "Please select a vendor.";
    if (form.codeHSNOverride && !form.codeHSN.trim()) e.codeHSN = "HSN code is required when override is enabled.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, ...form });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-product-modal">
        {/* Header */}
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><ProductIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Product" : "New Product"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update product details below." : "Fill in details to add a new product."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="rv-modal-body">

          {/* Product Code + Product Name */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Product Code <span className="rv-required">*</span></label>
              <input
                className={`rv-input ${errors.productCode ? "rv-input--err" : ""}`}
                placeholder="e.g. RJ-RING-001"
                value={form.productCode}
                onChange={e => set("productCode", e.target.value)}
              />
              {errors.productCode && <p className="rv-field-err">{errors.productCode}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Product Name <span className="rv-required">*</span></label>
              <input
                className={`rv-input ${errors.productName ? "rv-input--err" : ""}`}
                placeholder="e.g. Gold Solitaire Ring"
                value={form.productName}
                onChange={e => set("productName", e.target.value)}
              />
              {errors.productName && <p className="rv-field-err">{errors.productName}</p>}
            </div>
          </div>

          {/* Category + Vendor */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Category <span className="rv-required">*</span></label>
              <div className="rv-select-wrap">
                <select
                  className={`rv-select ${errors.categoryID ? "rv-input--err" : ""}`}
                  value={form.categoryID}
                  onChange={e => set("categoryID", Number(e.target.value))}
                >
                  <option value="">Select category…</option>
                  {categories.map(c => (
                    <option key={c.categoryID} value={c.categoryID}>{c.categoryName}</option>
                  ))}
                </select>
                <span className="rv-select-arrow"><ChevronIcon /></span>
              </div>
              {errors.categoryID && <p className="rv-field-err">{errors.categoryID}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Vendor <span className="rv-required">*</span></label>
              <div className="rv-select-wrap">
                <select
                  className={`rv-select ${errors.vendorID ? "rv-input--err" : ""}`}
                  value={form.vendorID}
                  onChange={e => set("vendorID", Number(e.target.value))}
                >
                  <option value="">Select vendor…</option>
                  {vendors.map(v => (
                    <option key={v.vendorID} value={v.vendorID}>{v.vendorName}</option>
                  ))}
                </select>
                <span className="rv-select-arrow"><ChevronIcon /></span>
              </div>
              {errors.vendorID && <p className="rv-field-err">{errors.vendorID}</p>}
            </div>
          </div>

          {/* Pricing Type + Purity Standard */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Pricing Type</label>
              <div className="rv-select-wrap">
                <select
                  className="rv-select"
                  value={form.pricingType}
                  onChange={e => set("pricingType", e.target.value)}
                >
                  <option value="">Select type…</option>
                  {PRICING_TYPES.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <span className="rv-select-arrow"><ChevronIcon /></span>
              </div>
            </div>
            <div className="rv-field">
              <label className="rv-label">Purity Standard</label>
              <div className="rv-select-wrap">
                <select
                  className="rv-select"
                  value={form.purityStandard}
                  onChange={e => set("purityStandard", e.target.value)}
                >
                  <option value="">Select purity…</option>
                  {PURITY_STANDARDS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <span className="rv-select-arrow"><ChevronIcon /></span>
              </div>
            </div>
          </div>

          {/* Barcode Type */}
          <div className="rv-field">
            <label className="rv-label">Barcode Type</label>
            <div className="rv-select-wrap">
              <select
                className="rv-select"
                value={form.barcodeType}
                onChange={e => set("barcodeType", e.target.value)}
              >
                <option value="">Select barcode type…</option>
                {BARCODE_TYPES.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              <span className="rv-select-arrow"><ChevronIcon /></span>
            </div>
          </div>

          {/* HSN Override toggle + conditional HSN input */}
          <div className="rv-field rv-field--row">
            <div>
              <label className="rv-label">HSN Code Override</label>
              <p className="rv-field-hint">Override the category-level HSN code for this product.</p>
            </div>
            <button
              type="button"
              className={`rv-toggle ${form.codeHSNOverride ? "rv-toggle--on" : ""}`}
              onClick={() => set("codeHSNOverride", !form.codeHSNOverride)}
            >
              <span className="rv-toggle-knob" />
            </button>
          </div>

          {form.codeHSNOverride && (
            <div className="rv-field">
              <label className="rv-label">HSN Code <span className="rv-required">*</span></label>
              <input
                className={`rv-input ${errors.codeHSN ? "rv-input--err" : ""}`}
                placeholder="e.g. 7113"
                value={form.codeHSN}
                onChange={e => set("codeHSN", e.target.value)}
              />
              {errors.codeHSN && <p className="rv-field-err">{errors.codeHSN}</p>}
            </div>
          )}

          {/* Is Active toggle */}
          <div className="rv-field rv-field--row">
            <div>
              <label className="rv-label">Active</label>
              <p className="rv-field-hint">Inactive products are hidden from sale workflows.</p>
            </div>
            <button
              type="button"
              className={`rv-toggle ${form.isActive ? "rv-toggle--on" : ""}`}
              onClick={() => set("isActive", !form.isActive)}
            >
              <span className="rv-toggle-knob" />
            </button>
          </div>

        </div>

        {/* Footer */}
        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN PRODUCT PAGE
══════════════════════════════════════ */
export default function ProductPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [toast, setToast] = useState(null);

  /* ── Resizable columns ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  // Columns: #, Code, Name, Category, Vendor, Pricing, Purity, Status, Actions
  useResizableColumns(tableRef, tableReady, [50, 130, 180, 150, 150, 120, 110, 100, 100]);

  /* ── Fetch helpers ── */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const [prodData, catData, venData] = await Promise.all([
        productApi.getAll(),
        productCategoryApi.getAll(),
        vendorApi.getAll(),
      ]);
      setProducts(Array.isArray(prodData) ? prodData : prodData?.data ?? []);
      setCategories(Array.isArray(catData) ? catData : catData?.data ?? []);
      setVendors(Array.isArray(venData) ? venData : venData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load products.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Lookup helpers ── */
  const getCategoryName = (id) => categories.find(c => c.categoryID === id)?.categoryName ?? "—";
  const getVendorName = (id) => vendors.find(v => v.vendorID === id)?.vendorName ?? "—";

  /* ── Save ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.productID) {
        await productApi.update(formData);
        showToast("Product updated successfully.");
      } else {
        await productApi.create(formData);
        showToast("Product created successfully.");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await productApi.remove(confirm.id);
      showToast("Product deleted.");
      setConfirm(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      p.productName?.toLowerCase().includes(q) ||
      p.productCode?.toLowerCase().includes(q) ||
      p.pricingType?.toLowerCase().includes(q) ||
      p.purityStandard?.toLowerCase().includes(q) ||
      getCategoryName(p.categoryID).toLowerCase().includes(q) ||
      getVendorName(p.vendorID).toLowerCase().includes(q);
    const matchStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && p.isActive) ||
      (filterStatus === "inactive" && !p.isActive);
    return matchSearch && matchStatus;

  });
const {
    paged, page, totalPages, pageSize,
    setPage, setPageSize,
    pageStart, pageEnd, totalItems,
  } = usePagination(filtered, 10);
  return (
    <div className="rv-product-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {confirm && (
        <ConfirmDialog
          name={confirm.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <ProductModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          categories={categories}
          vendors={vendors}
        />
      )}
      {drawer && (
        <ProductDrawer
          product={drawer}
          categories={categories}
          vendors={vendors}
          onClose={() => setDrawer(null)}
          onEdit={p => setModal({ data: p })}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-product-topbar">
        <div className="rv-product-topbar-left">
          <h2 className="rv-product-heading">Products</h2>
          <span className="rv-product-count">{products.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Product
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rv-product-filters">
        <div className="rv-product-search">
          <SearchIcon />
          <input
            placeholder="Search by name, code, category or vendor…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-product-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div className="rv-filter-tabs">
          {[
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ].map(s => (
            <button
              key={s.value}
              className={`rv-filter-tab ${filterStatus === s.value ? "rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterStatus(s.value); setPage(1); }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-product-card">
        {loading ? (
          <div className="rv-product-empty">
            <div className="rv-loader" />
            <p>Loading products…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rv-product-empty">
            <div className="rv-empty-icon"><ProductIcon /></div>
            <p className="rv-empty-title">No products found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search term." : "Add your first product to get started."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                <PlusIcon /> New Product
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="rv-table-wrap rv-product-table-wrap">
              <table className="rv-table rv-product-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Vendor</th>
                    <th>Pricing</th>
                    <th>Purity</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p, i) => (
                    <tr
                      key={p.productID}
                      className="rv-product-row"
                      onClick={() => setDrawer(p)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="rv-product-idx">{pageStart + i}</td>

                      {/* Code */}
                      <td>
                        <span className="rv-product-code">{p.productCode || <span className="rv-empty-val">—</span>}</span>
                      </td>

                      {/* Name */}
                      <td>
                        <div className="rv-product-name-cell">
                          {/* <div className="rv-product-avatar-sm">
                          {p.productName?.charAt(0).toUpperCase()}
                        </div> */}
                          <span className="rv-product-name">{p.productName}</span>
                        </div>
                      </td>

                      {/* Category */}
                      <td>
                        <span className="rv-product-category">{getCategoryName(p.categoryID)}</span>
                      </td>

                      {/* Vendor */}
                      <td>
                        <span className="rv-product-vendor">{getVendorName(p.vendorID)}</span>
                      </td>

                      {/* Pricing Type */}
                      <td>
                        {p.pricingType
                          ? <span className="rv-product-badge">{p.pricingType}</span>
                          : <span className="rv-empty-val">—</span>
                        }
                      </td>

                      {/* Purity */}
                      <td>
                        {p.purityStandard
                          ? <span className="rv-product-purity">{p.purityStandard}</span>
                          : <span className="rv-empty-val">—</span>
                        }
                      </td>

                      {/* Status */}
                      <td>
                        <span className={`rv-status-pill ${p.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                          <span className="rv-status-dot" />
                          {p.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="rv-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className="rv-action-btn rv-action-btn--edit"
                            title="Edit"
                            onClick={() => setModal({ data: p })}
                          >
                            <EditIcon />
                          </button>
                          <button
                            className="rv-action-btn rv-action-btn--del"
                            title="Delete"
                            onClick={() => setConfirm({ id: p.productID, name: p.productName })}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              setPage={setPage}
              setPageSize={setPageSize}
              pageStart={pageStart}
              pageEnd={pageEnd}
              totalItems={totalItems}
            />
          </>
        )}
      </div>

      {/* ── Resize hint ── */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint">
          <GripIcon /> Drag column borders to resize · Click a row to view details
        </p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function ChevronIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>; }
function ProductIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>; }
function VendorIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>; }
function LayersIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>; }
function CurrencyIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>; }
function StarIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>; }
function BarcodeIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 5v14M7 5v14M11 5v14M15 5v14M19 5v14M21 5v14" /></svg>; }
function HashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></svg>; }
function TagIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>; }